import React, { createContext, useContext, useState, useCallback } from 'react';

interface FOVOverlayContextValue {
  activePortraitGroupId: string | null;
  /**
   * A portrait group can be fixed-camera, and the exporter deliberately leaves
   * those clips' framing alone (they carry the operator's own pan/tilt/zoom),
   * so the overlay must draw the un-zoomed region for them — C360-3094.
   */
  activeGroupIsFixedCamera: boolean;
  setActivePortraitGroupId: (
    groupId: string | null,
    options?: { fixedCamera?: boolean },
  ) => void;
}

const FOVOverlayContext = createContext<FOVOverlayContextValue | undefined>(undefined);

export const FOVOverlayProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [active, setActive] = useState<{
    groupId: string | null;
    fixedCamera: boolean;
  }>({ groupId: null, fixedCamera: false });

  const setActivePortraitGroupId = useCallback(
    (groupId: string | null, options?: { fixedCamera?: boolean }) => {
      setActive({
        groupId,
        fixedCamera: groupId == null ? false : options?.fixedCamera === true,
      });
    },
    [],
  );

  const value = {
    activePortraitGroupId: active.groupId,
    activeGroupIsFixedCamera: active.fixedCamera,
    setActivePortraitGroupId,
  };

  return (
    <FOVOverlayContext.Provider value={value}>
      {children}
    </FOVOverlayContext.Provider>
  );
};

export const useFOVOverlay = () => {
  const context = useContext(FOVOverlayContext);
  if (!context) {
    throw new Error('useFOVOverlay must be used within FOVOverlayProvider');
  }
  return context;
};
